/**
 * Semantic Z-Index-Layers für den public/-Viewer-Stack.
 *
 * Foundation für Public-Stack-Chrome-Sprint (D1-D4, 2026-05-21).
 * Drift-Map-Diagnose hat z-Index-Drift identifiziert: app/-Stack benutzt
 * z-[1002]/2000/9999/100; public/-Stack benutzt 6/15/20/30/40/1000.
 * Sprint-Ziel: gemeinsame semantische Skala für die neuen public/-Chrome-
 * Komponenten (StartGate, CompanyLogo, StoryFooter, CreditsOverlay).
 *
 * Bestandsschutz: existierende z-Index-Werte (6, 15, 20, 30, 40, 1000)
 * bleiben unverändert in ViewerChrome / ResumeToast / SignInModal. Nur
 * NEUE Komponenten nutzen die `Z`-Konstante. Migration der existierenden
 * Werte zu `Z.*`-Konstanten ist Plan-2-Backlog (additiv, kein Refactor-
 * Risiko mit-zubrennen).
 *
 * Layer-Reihenfolge (bottom → top):
 *
 *   background  =   0  PhoneFrame-Hintergrund, ImmersiveCanvas-Backdrop
 *   progressBar =   6  Inline progress fill (existing)
 *   hairline    =  15  KeyboardHint (existing)
 *   chrome      =  20  Stage-Container, ProgressHairline (existing)
 *   notch       =  30  PhoneFrame-Notch, TopBar (existing)
 *   toast       =  40  ResumeToast (existing)
 *   credits     =  50  CreditsOverlay (D4, Slide-Level Asset-Credits) — NEW
 *   footer      =  60  StoryFooter im content-bottom-Overlay (D3) — NEW
 *   logo        = 2000 CompanyLogo fixed-top, oberhalb Chrome (D2)
 *                 (Plan-N2 2026-05-25 Bene: von 80 auf 2000 angehoben.
 *                 Der frühere 80er-Wert ging davon aus, das Logo läge
 *                 zwischen Chrome (≤60) und Modal (1000). Aber
 *                 VisualStoryPlayer im Renderer hat eigene hohe z-Indexe
 *                 mitgebracht — sticky-Container z-998, scrollOuter z-1000,
 *                 inner-sticky z-1200, CoverSection/StartScreen z-1002 —
 *                 alle innerhalb desselben Wrapper-Stacking-Contexts.
 *                 Bene-Befund "Logo nur sporadisch sichtbar" war die
 *                 Konsequenz: an den Stellen wo der Renderer Inhalt hat,
 *                 wurde Logo verdeckt. z-2000 deckt alle Renderer-Layer
 *                 ab und entspricht dem app/-Stack-Wert in
 *                 packages/viewer/CompanyLogo.tsx:49.)
 *   startGate   = 200  Initial-Overlay vor User-Klick (D1) — NEW
 *   modal       = 10000 SignInModal, Future Lightboxes
 *                 (Plan-N2 2026-05-25: von 1000 auf 10000 angehoben, weil
 *                 packages/viewer-StartScreen + public/CoverSection mit
 *                 z-[1002] innerhalb des ViewerChrome-Stacking-Contexts
 *                 1002 > 1000 hatten und das Modal links/rechts neben dem
 *                 PhoneFrame "rausleakte" statt davor zu liegen.)
 *
 * Semantik:
 * - StartGate < Modal: wenn Modal während des Start-Gates aufgeht
 *   (z.B. Session-Expired-Reauth), Modal liegt darüber.
 * - Logo > Chrome: CompanyLogo darf TopBar überlagern (Branding-Priorität).
 * - Footer < Logo: StoryFooter ist content-bound (bottom-of-content-Stream),
 *   nicht globales Chrome.
 * - Credits < Footer: Slide-Credits sind transient (kommen+gehen mit Slide-
 *   Position), bleiben unter dem persistenten Footer.
 */

export const Z = {
  background: 0,
  progressBar: 6,
  hairline: 15,
  chrome: 20,
  notch: 30,
  toast: 40,
  credits: 50,
  footer: 60,
  logo: 2000,
  startGate: 200,
  modal: 10000,
} as const;

export type ZLayer = keyof typeof Z;
